import { Component, OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormGroup, FormArray, FormBuilder, FormControl } from '@angular/forms';
import { UserMasterService } from './../../../services/user-master.service';
import { CompanyMasterService } from './../../../services/companymaster.service';
import * as _ from 'lodash';

@Component({
  selector: 'app-copy-user-to-companies-modal',
  templateUrl: './copy-user-to-companies-modal.component.html',
  styleUrls: ['./copy-user-to-companies-modal.component.css']
})
export class CopyUserToCompaniesModalComponent implements OnInit {

  @Input('user') user: any; // The user to copy

  public allCompaniesList: any[] = [];
  public selectedCompanies: any[] = [];
  public companyFormArray: FormArray;
  public companyForm: FormGroup;
  
  // User data for pre-population
  public userType: string = '';
  public userDesignation: string = '';
  public userDirectorType: string = '';
  public userCategory: string = '';
  public isBoardMember: boolean = false;
  public isKMP: boolean = false;
  
  // Lists for dropdowns
  public directorTypesList: any[] = [];
  public boardDesignationlist: any[] = [];
  public kmpDesignationList: any[] = [];
  public categoryList: any[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private userMasterService: UserMasterService,
    private companyMasterService: CompanyMasterService
  ) {
    this.companyForm = this.fb.group({
      Companies: new FormArray([])
    });
    this.companyFormArray = this.companyForm.get('Companies') as FormArray;
  }

  ngOnInit() {
    this.loadDropdownLists();
    this.fetchUserDetails();
  }

  fetchUserDetails() {
    // Fetch full user details to get Designation, DirectorType, Category
    const userId = this.user._id || this.user.UserId || this.user.userId;
    if (userId) {
      this.userMasterService.getUserByUserId(userId).then((response: any) => {
        if (response && response.status === 0 && response.userDetails) {
          // Update user object with full details
          this.user = { ...this.user, ...response.userDetails };
        }
        this.initializeUserData();
        this.loadAllCompanies();
      }).catch((error) => {
        console.error('Error fetching user details:', error);
        // If fetch fails, use the data from the table
        this.initializeUserData();
        this.loadAllCompanies();
      });
    } else {
      // If no userId, use the data from the table
      this.initializeUserData();
      this.loadAllCompanies();
    }
  }

  initializeUserData() {
    if (this.user) {
      this.userType = this.user.TypeOfUser || this.user.userType || '';
      
      // Try multiple possible field names for designation
      this.userDesignation = this.user.DesignationSelection || this.user.Designation || '';
      
      // Try multiple possible field names for director type
      this.userDirectorType = this.user.DirectorType || '';
      
      // Try multiple possible field names for category
      this.userCategory = this.user.Category || '';
      
      // Determine if user is Board Member or KMP
      this.isBoardMember = this.userType === 'BoardMember' || this.userType === 'Board Member';
      this.isKMP = this.userType === 'KMP';
    }
  }

  loadDropdownLists() {
    // Load director types
    this.directorTypesList = [
      { value: 'ExcecutiveDirector', label: 'Executive Director' },
      { value: 'NonExcecutiveDirector', label: 'Non-Executive Director' }
    ];

    // Load KMP designations
    this.kmpDesignationList = [
      { label: "Chief Executive Officer", value: "Chief Executive Officer" },
      { label: "Chief Financial Officer", value: "Chief Financial Officer" },
      { label: "Company Secretary", value: "Company Secretary" },
      { label: "Manager", value: "Manager" },
      { label: "Managing Director", value: "Managing Director" },
      { label: "Others", value: "Others" },
      { label: "Whole Time Director", value: "Whole Time Director" },
    ];

    // Load Board Member designations (combining executive and non-executive)
    const executiveDesignationlist = [
      { label: "Managing Director", value: "Managing Director" },
      { label: "Managing Director (Additional Director)", value: "Managing Director (Additional Director)" },
      { label: "Whole Time Director", value: "Whole Time Director" },
      { label: 'Whole Time Director (Additional Director)', value: 'Whole Time Director (Additional Director)' }
    ];

    const nonExecutiveDesignationlist = [
      { label: "Additional Director", value: "Additional Director" },
      { label: "Alternate Director", value: "Alternate Director" },
      { label: "Director", value: "Director" },
      { label: 'Director Appointed in Casual Vacancy', value: 'Director Appointed in Casual Vacancy' },
      { label: "Nominee Director", value: "Nominee Director" }
    ];

    // Combine both lists for board designations
    this.boardDesignationlist = [...executiveDesignationlist, ...nonExecutiveDesignationlist];

    // Load category list
    this.fillCategoryList();
  }

  fillCategoryList() {
    this.categoryList = [
      {
        value: 'Independent',
        label: 'Independent'
      },
      {
        value: 'Promoter',
        label: 'Promoter'
      },
      {
        value: 'Professional',
        label: 'Professional'
      }
    ];
  }

  loadAllCompanies() {
    this.userMasterService.getCompanyList().then((response: any) => {
      if (response && response.status === 0 && response.companyList) {
        this.allCompaniesList = response.companyList;
        
        // Initialize selected companies array
        this.selectedCompanies = this.allCompaniesList.map(company => ({
          companyId: company._id || company.CompanyId,
          companyName: company.CompanyName,
          isSelected: false,
          isBoardMember: this.isBoardMember, // Pre-select based on user type
          isKMP: this.isKMP, // Pre-select based on user type
          designation: this.userDesignation || '',
          directorType: this.userDirectorType || '',
          category: this.userCategory || ''
        }));
        
        // Initialize form array for each company with pre-populated values
        this.companyFormArray.clear();
        this.selectedCompanies.forEach((company) => {
          this.companyFormArray.push(this.fb.group({
            isSelected: [false],
            isBoardMember: [company.isBoardMember],
            isKMP: [company.isKMP],
            designation: [company.designation || ''], // Pre-populated from user data
            directorType: [company.directorType || ''], // Pre-populated from user data
            category: [company.category || ''] // Pre-populated from user data
          }));
        });
        
        // Force update the form controls to reflect the values after view initialization
        setTimeout(() => {
          this.selectedCompanies.forEach((company, index) => {
            if (company.designation) {
              this.companyFormArray.at(index).get('designation').setValue(company.designation, { emitEvent: false });
            }
            if (company.directorType) {
              this.companyFormArray.at(index).get('directorType').setValue(company.directorType, { emitEvent: false });
            }
            if (company.category) {
              this.companyFormArray.at(index).get('category').setValue(company.category, { emitEvent: false });
            }
          });
        }, 200);
      }
    }).catch((error) => {
      console.error('Error loading companies:', error);
      // Fallback: try companyMasterService if userMasterService fails
      this.companyMasterService.getCompanyList().then((response: any) => {
        if (response && response.status === 0 && response.companyData) {
          if (!Array.isArray(response.companyData)) {
            this.allCompaniesList = [response.companyData];
          } else {
            this.allCompaniesList = response.companyData;
          }
          
          this.selectedCompanies = this.allCompaniesList.map(company => ({
            companyId: company._id,
            companyName: company.CompanyName,
            isSelected: false,
            isBoardMember: this.isBoardMember,
            isKMP: this.isKMP,
            designation: this.userDesignation || '',
            directorType: this.userDirectorType || '',
            category: this.userCategory || ''
          }));
          
          this.companyFormArray.clear();
          this.selectedCompanies.forEach(() => {
            this.companyFormArray.push(this.fb.group({
              isSelected: [false],
              isBoardMember: [this.isBoardMember],
              isKMP: [this.isKMP],
              designation: [this.userDesignation || ''],
              directorType: [this.userDirectorType || ''],
              category: [this.userCategory || '']
            }));
          });
        }
      });
    });
  }

  toggleSelectAllCompanies(event: any) {
    const isChecked = event.target.checked;
    this.selectedCompanies.forEach((company, index) => {
      company.isSelected = isChecked;
      this.companyFormArray.at(index).patchValue({ isSelected: isChecked });
      
      if (!isChecked) {
        company.isBoardMember = this.isBoardMember;
        company.isKMP = this.isKMP;
        company.designation = this.userDesignation || '';
        company.directorType = this.userDirectorType || '';
        company.category = this.userCategory || '';
        this.companyFormArray.at(index).patchValue({
          isBoardMember: this.isBoardMember,
          isKMP: this.isKMP,
          designation: this.userDesignation || '',
          directorType: this.userDirectorType || '',
          category: this.userCategory || ''
        });
      }
    });
  }

  onCompanySelectionChange(index: number, event: any) {
    const isChecked = event.target.checked;
    this.selectedCompanies[index].isSelected = isChecked;
    this.companyFormArray.at(index).patchValue({ isSelected: isChecked });
    
    if (!isChecked) {
      this.selectedCompanies[index].isBoardMember = this.isBoardMember;
      this.selectedCompanies[index].isKMP = this.isKMP;
      this.selectedCompanies[index].designation = this.userDesignation || '';
      this.selectedCompanies[index].directorType = this.userDirectorType || '';
      this.selectedCompanies[index].category = this.userCategory || '';
      this.companyFormArray.at(index).patchValue({
        isBoardMember: this.isBoardMember,
        isKMP: this.isKMP,
        designation: this.userDesignation || '',
        directorType: this.userDirectorType || '',
        category: this.userCategory || ''
      });
    }
  }

  onBoardMemberChange(index: number, event: any) {
    const isChecked = event.target.checked;
    this.selectedCompanies[index].isBoardMember = isChecked;
    this.companyFormArray.at(index).patchValue({ isBoardMember: isChecked });
    
    if (!isChecked && !this.selectedCompanies[index].isKMP) {
      this.selectedCompanies[index].designation = '';
      this.companyFormArray.at(index).patchValue({ designation: '' });
    }
    
    if (!isChecked) {
      this.selectedCompanies[index].directorType = '';
      this.selectedCompanies[index].category = '';
      this.companyFormArray.at(index).patchValue({ 
        directorType: '',
        category: ''
      });
    }
  }

  onKMPChange(index: number, event: any) {
    const isChecked = event.target.checked;
    this.selectedCompanies[index].isKMP = isChecked;
    this.companyFormArray.at(index).patchValue({ isKMP: isChecked });
    
    if (!isChecked && !this.selectedCompanies[index].isBoardMember) {
      this.selectedCompanies[index].designation = '';
      this.companyFormArray.at(index).patchValue({ designation: '' });
    }
  }

  onCompanyDesignationChange(index: number, value: string) {
    this.selectedCompanies[index].designation = value;
    this.companyFormArray.at(index).patchValue({ designation: value });
  }

  onCompanyDirectorTypeChange(index: number, value: string) {
    this.selectedCompanies[index].directorType = value;
    this.companyFormArray.at(index).patchValue({ directorType: value });
  }

  onCompanyCategoryChange(index: number, value: string) {
    this.selectedCompanies[index].category = value;
    this.companyFormArray.at(index).patchValue({ category: value });
  }

  getSelectedCompaniesCount(): number {
    return this.selectedCompanies.filter(c => c.isSelected).length;
  }

  areAllCompaniesSelected(): boolean {
    return this.selectedCompanies.length > 0 && this.selectedCompanies.every(c => c.isSelected);
  }

  areSomeCompaniesSelected(): boolean {
    return this.selectedCompanies.some(c => c.isSelected) && !this.selectedCompanies.every(c => c.isSelected);
  }

  save() {
    const selectedCompaniesData = this.selectedCompanies
      .filter(company => company.isSelected)
      .map(company => ({
        companyId: company.companyId,
        companyName: company.companyName,
        isBoardMember: company.isBoardMember,
        isKMP: company.isKMP,
        designation: company.designation,
        directorType: company.directorType,
        category: company.category
      }));

    // Return the selected companies data
    this.activeModal.close({
      status: 0,
      selectedCompanies: selectedCompaniesData,
      userId: this.user._id || this.user.UserId
    });
  }

  cancel() {
    this.activeModal.dismiss();
  }
}

